import { BadgeCheck, Building2, Cable, Palette, ScrollText, Users as UsersIcon } from 'lucide-react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { SettingsSidebar, type SettingsNavItem } from '../components/SettingsSidebar.tsx'
import type { Me } from '../types.ts'
import { Appearance } from './Appearance.tsx'
import { CompanySettings } from './CompanySettings.tsx'
import { Licenses } from './Licenses.tsx'
import { Logs } from './Logs.tsx'
import { McpServer } from './McpServer.tsx'
import { McpServers } from './McpServers.tsx'
import { Users } from './Users.tsx'

// Badge colors: Users wears the accent (design.md section 9's Account slot), Licenses dark
// green, MCP Servers a white badge with a black icon, Company blue, Logs neutral grey
// (Advanced), Appearance purple.
const USERS: SettingsNavItem = { to: '/settings/users', label: 'Users', icon: UsersIcon, color: 'var(--color-accent)' }
const LICENSES: SettingsNavItem = { to: '/settings/licenses', label: 'Licenses', icon: BadgeCheck, color: '#1F7A3A' }
const MCP_SERVERS: SettingsNavItem = { to: '/settings/mcp-servers', label: 'MCP Servers', icon: Cable, color: '#FFFFFF', iconColor: '#000000' }
const COMPANY: SettingsNavItem = { to: '/settings/company', label: 'Company', icon: Building2, color: 'var(--color-system-blue)' }
const LOGS: SettingsNavItem = { to: '/settings/logs', label: 'Logs', icon: ScrollText, color: 'var(--color-text-tertiary)' }
const APPEARANCE: SettingsNavItem = { to: '/settings/appearance', label: 'Appearance', icon: Palette, color: 'var(--color-system-purple)' }

/** Settings, reached from the account menu (design.md 8.8). Everyone has Appearance; anyone in
 *  a company sees its MCP servers; a company administrator also manages users, licenses and
 *  the company here. */
export function Settings({ me }: { me: Me }) {
  const current = me.current
  const companyId = current?.company.id ?? null
  const admin = current?.person.iceRole === 'company_admin'
  const items = admin
    ? [USERS, LICENSES, MCP_SERVERS, COMPANY, LOGS, APPEARANCE]
    : current
      ? [MCP_SERVERS, APPEARANCE]
      : [APPEARANCE]
  const home = admin ? 'users' : current ? 'mcp-servers' : 'appearance'
  return (
    <div className="settings-layout">
      <SettingsSidebar title="Settings" items={items} />
      <div className="settings-pane">
        <Routes>
          {admin && companyId && <Route path="users" element={<Users companyId={companyId} />} />}
          {admin && companyId && <Route path="licenses" element={<Licenses companyId={companyId} />} />}
          {admin && companyId && <Route path="company" element={<CompanySettings companyId={companyId} />} />}
          {admin && companyId && <Route path="logs" element={<Logs companyId={companyId} />} />}
          {current && <Route path="mcp-servers" element={<McpServers me={me} />} />}
          {current && <Route path="mcp-servers/:appId" element={<McpServer me={me} />} />}
          <Route path="appearance" element={<Appearance />} />
          <Route path="*" element={<Navigate to={home} replace />} />
        </Routes>
      </div>
    </div>
  )
}
